// Map.js - Complete updated version
import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './Map.css';
import AuthModal from './AuthModal';
import { Target, Map as MapIcon, LogOut, User, Menu, ChevronLeft } from 'lucide-react';

function Map() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get current user info and check authentication
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setCurrentUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing user data:', error);
        handleLogout();
      }
    }
  }, []);
  
  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };
    
    // Initial dimensions
    updateDimensions();
    
    // Add event listener for window resize
    window.addEventListener('resize', updateDimensions);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);
  
  // Fetch all users data
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch('/api/users/all', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }
        
        const data = await response.json();
        setUsers(data);
        
        // Calculate connections based on shared images
        const connections = calculateConnections(data);
        setConnections(connections);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching users:', error);
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, []);
  
  // Calculate connections between users based on shared images
  const calculateConnections = (users) => {
    const connections = [];
    
    // For each pair of users
    for (let i = 0; i < users.length; i++) {
      for (let j = i + 1; j < users.length; j++) {
        const userA = users[i];
        const userB = users[j];
        
        // Get sets of image IDs
        const imagesA = new Set(userA.savedImages.map(img => img.archiveId));
        const imagesB = new Set(userB.savedImages.map(img => img.archiveId));
        
        // Find shared images (intersection)
        const sharedImages = [...imagesA].filter(id => imagesB.has(id));
        
        // Only create connection if they share images
        if (sharedImages.length > 0) {
          connections.push({
            source: userA.id,
            target: userB.id,
            strength: sharedImages.length,
            sharedImages: sharedImages
          });
        }
      }
    }
    
    return connections;
  };
  
  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setCurrentUser(null);
    window.location.href = '/';
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const toggleSidebar = () => {
    setIsSidebarMinimized(!isSidebarMinimized);
  };

  const searchMatches = searchQuery.trim()
    ? users
        .filter(u => u.username.toLowerCase().includes(searchQuery.trim().toLowerCase()))
        .slice(0, 8)
    : [];

  const goToUser = (username) => {
    window.location.href = `/${encodeURIComponent(username)}`;
  };
  
  // Set up D3 visualization when data is loaded and dimensions change
  useEffect(() => {
    if (loading || users.length === 0 || dimensions.width === 0) return;
    
    const svg = d3.select(svgRef.current);
    
    // Set SVG dimensions
    svg.attr('width', dimensions.width)
       .attr('height', dimensions.height);
    
    // Clear previous content
    svg.selectAll('*').remove();
    
    // Create main group for zoom/pan
    const g = svg.append('g');
    
    // Force-directed layout: repulsion spaces users out, link forces pull
    // users who share saved images into clusters.
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;

    // Copies — d3 mutates nodes (x/y) and links (source/target -> node refs)
    const userNodes = users.map(user => ({ ...user }));
    const linkData = connections.map(c => ({ ...c }));

    // Draw connections (positions set on each simulation tick)
    const links = g.selectAll('.connection')
      .data(linkData)
      .enter()
      .append('line')
      .attr('class', 'connection')
      .attr('stroke-width', d => Math.sqrt(d.strength));

    // Create user nodes
    const nodeGroups = g.selectAll('.node')
      .data(userNodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .style('cursor', 'pointer');
    
    // Add circular clipping path for profile pictures
    nodeGroups.append('clipPath')
      .attr('id', d => `clip-${d.id}`)
      .append('circle')
      .attr('r', 30);
    
    // Add profile pictures
    nodeGroups.append('image')
      .attr('href', d => d.profilePicture?.path 
        ? `/uploads/${d.profilePicture.path}` 
        : '/default-profile.png')
      .attr('clip-path', d => `url(#clip-${d.id})`)
      .attr('width', 60)
      .attr('height', 60)
      .attr('x', -30)
      .attr('y', -30);
    
    // Add usernames
    nodeGroups.append('text')
      .text(d => d.username)
      .attr('text-anchor', 'middle')
      .attr('dy', 45);
    
    // Add click handler for navigation
    nodeGroups.on('click', (event, d) => {
      event.stopPropagation();
      window.location.href = `/${d.username}`;
    });
    
    // Run the force simulation
    const simulation = d3.forceSimulation(userNodes)
      .force('link', d3.forceLink(linkData)
        .id(d => d.id)
        .distance(d => Math.max(100, 260 - d.strength * 25))
        .strength(d => Math.min(1, 0.3 + d.strength * 0.1)))
      .force('charge', d3.forceManyBody().strength(-500))
      .force('collide', d3.forceCollide(70))
      .force('x', d3.forceX(centerX).strength(0.06))
      .force('y', d3.forceY(centerY).strength(0.06))
      .on('tick', () => {
        links
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y);
        nodeGroups.attr('transform', d => `translate(${d.x}, ${d.y})`);
      });

    // Add zoom/pan functionality
    const zoom = d3.zoom()
      .scaleExtent([0.2, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    return () => simulation.stop();

  }, [users, connections, loading, dimensions]);
  
  return (
    <div className="page-container">
      {/* Sidebar with minimize functionality */}
      <div className={`sidebar ${isSidebarMinimized ? 'minimized' : ''}`}>
        {!isSidebarMinimized ? (
          <div className="sidebar-nav">
            <button className="nav-button minimize-button" onClick={toggleSidebar} title="Minimize sidebar">
              <ChevronLeft size={18} />
            </button>
            {isAuthenticated ? (
              <>
                <a href="/" className="nav-button" title="Home"><Target size={18} /></a>
                <a href={currentUser ? `/${currentUser.username}` : '/dashboard'} className="nav-button" title="Profile"><User size={18} /></a>
                <a href="/map" className="nav-button active" title="Map"><MapIcon size={18} /></a>
                <button className="nav-button" onClick={handleLogout} title="Logout"><LogOut size={18} /></button>
              </>
            ) : (
              <button className="nav-button" onClick={toggleModal} title="Login"><User size={18} /></button>
            )}
          </div>
        ) : (
          <button className="nav-button expand-button" onClick={toggleSidebar} title="Expand sidebar">
            <Menu size={18} />
          </button>
        )}
      </div>

      {/* User search */}
      <div className="map-search">
        <input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && searchMatches.length > 0) {
              goToUser(searchMatches[0].username);
            }
          }}
        />
        {searchMatches.length > 0 && (
          <ul className="map-search-results">
            {searchMatches.map(u => (
              <li key={u.id} onClick={() => goToUser(u.username)}>
                {u.username}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Map Container */}
      <div className="map-container" ref={containerRef}>
        {loading ? (
          <div className="loading">Loading user map...</div>
        ) : (
          <svg ref={svgRef} className="user-map"></svg>
        )}
      </div>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showModal} 
        onClose={(success, userData) => {
          setShowModal(false);
          if (success && userData) {
            setIsAuthenticated(true);
            setCurrentUser(userData);
          }
        }}
      />
    </div>
  );
}

export default Map;